import * as PIXI from 'pixi.js';

import { BufferingMode } from '../../../enums';
import type { SeekMode } from '../../../enums';
import type { Transformation as TransformationType } from '../../../types';
import { Config } from '../../../video-data/config';
import { getPositionFromTransformation } from '../../../video-data/designs/scenes';
import type { LoadSettings } from '../../serializableMixin';
import { DynamicStylingMixin } from '../../dynamicStylingMixin';
import type { BaseAssetSettings } from '../BaseAsset';
import { BaseAsset } from '../BaseAsset';
import { resetSprite } from '../utils';
import { HtmlCompositionRenderer } from './HtmlCompositionRenderer';
import { applyEditableStyle, editableValueToHtml, replaceEditableText } from './editableText';
import { isNodeVisible } from './editableVisibility';
import type { GsapTimelineLike } from './editableVisibility';
import { HtmlTextEditManager, EDITABLE_OVERLAY_STYLE_PROPS } from './HtmlTextEditManager';
import type { CaretPoint, EditableRegionDescriptor } from './HtmlTextEditManager';
import { lintHtmlComposition } from './lint/lintHtmlComposition';
import { runRuntimeLintRules } from './lint/runtimeRules';
import { canvasSizeOf, captureFrames } from './lint/measure';
import type { LintFinding } from './lint/types';
import {
    manageComposition,
    templateColorToHex,
    TEMPLATE_COLOR_VAR_PREFIX,
    TEMPLATE_FONT_VAR_PREFIX,
} from './htmlManagement';
import { getResolvedHtmlFontFaces, resolveHtmlFontFaces } from './htmlFonts';
import type { HtmlFontSlot } from './htmlFonts';

type HtmlAssetSettings = BaseAssetSettings;

/**
 * A user-editable text node (keyed by its element `id`); bounds are in reference-resolution space.
 *
 * Reading `data-l5-editable` is still supported for compositions that carry it, but the lint no
 * longer requires the label and the design agent no longer emits it — in-place text editing of a
 * generated scene is off, so newly authored compositions have no editable regions at all.
 */
export interface EditableTextRegion {
    id: string;
    text: string;
    bounds: { x: number; y: number; width: number; height: number };
    /** The node's current CSS translate (reference px), so the editor can accumulate in-place moves. */
    translate: { x: number; y: number };
    /** The node's content overflows its box (a fixed height shorter than the text), so it's clipped. */
    clipping: boolean;
}

export interface CodeoAssetRegion {
    label: string;
    bounds: { x: number; y: number; width: number; height: number };
    outerHtml: string;
}

/**
 * A text node explicitly marked `data-l5-editable="false"`: not editable in place, but its bounds let
 * the editor show a "this can't be edited here" affordance when the user clicks it.
 */
export interface NonEditableTextRegion {
    id: string;
    bounds: { x: number; y: number; width: number; height: number };
}

/**
 * The loaded composition, handed to `introspectComposition` so a caller outside luminary can read
 * and pose it: its document, its `window.__timelines.main` timeline (null when it declares none),
 * and the seek/rasterize pair the renderer drives it with.
 */
export interface CompositionIntrospection {
    readonly document: Document;
    readonly timeline: GsapTimelineLike | null;
    /** Seek the composition to normalized progress (0..1). */
    seek(progress: number): void;
    /** Rasterize the frame currently posed; `scale` multiplies the rasterized pixel size. */
    rasterize(scale: number): Promise<HTMLCanvasElement>;
}

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

/**
 * A scene asset whose content is an HTML + timeline composition. It loads into a hidden iframe, is
 * seeked by normalized progress, and is rasterized with snapDOM into a PixiJS texture, so the editor
 * preview and MP4 export share one path.
 */
export class HtmlAsset extends DynamicStylingMixin(BaseAsset) {
    override _classType = Config.HTML_ASSET_TYPE;

    #width: number;
    #height: number;

    #html = '';
    #managedHtml = '';
    #managedSourceHtml: string | null = null;
    #paletteKey = '';
    #fontsKey = '';
    #editingRegionId: string | null = null;
    #managedEditingRegionId: string | null = null;
    #htmlTextEditManager: HtmlTextEditManager | null = null;

    #progress = 0;

    #sprite: PIXI.Sprite;
    #renderer: HtmlCompositionRenderer;

    #loadedManagedHtml: string | null = null;

    #displayedCanvas: HTMLCanvasElement | null = null;

    // Latest-wins playback: one rasterize in flight; a tick mid-rasterize re-arms at the current
    // progress instead of queueing, and every completed frame is shown.
    #playbackRenderPending = false;
    #playbackRendering = false;

    // Editable regions measured at the last at-rest render's frame; exposed synchronously so the
    // editor reads them reactively (see `renderedEditableTextRegions`). Not touched during playback.
    #renderedEditableTextRegions: EditableTextRegion[] = [];

    // Non-editable text regions from that same at-rest frame; the editor uses their bounds to explain
    // (on click) that the text isn't editable in place. Refreshed alongside the editable regions.
    #renderedNonEditableTextRegions: NonEditableTextRegion[] = [];

    // Serialized: the single shared iframe can't rasterize concurrent frames.
    #rasterizeChain: Promise<void> = Promise.resolve();

    #externalTaskRunning = false;

    constructor(container: unknown, settings: HtmlAssetSettings) {
        super(container, settings);

        const { width, height } = this.getReferenceResolution();
        this.#width = width;
        this.#height = height;

        this.#renderer = new HtmlCompositionRenderer(this.#width, this.#height);
        this.#sprite = new PIXI.Sprite();
        this._addPixiChild(this.#sprite);
    }

    override destroy() {
        if (this.destroyed) {
            return;
        }
        this.#htmlTextEditManager?.removeOverlays();
        this.#htmlTextEditManager = null;
        this.#renderer.destroy();
        this.#displayedCanvas = null;
        super.destroy(false);
        this.#sprite.destroy(false);
    }

    override get _shouldLoad() {
        return true;
    }

    // Per-frame readiness is gated by the awaited _bufferAtTime; gating isLoaded on a rasterized
    // frame would deadlock onAssetsLoaded.
    override get isLoaded(): boolean {
        return true;
    }

    override get failedToLoad(): boolean {
        return false;
    }

    /** @internal */
    _hasRenderedFrame(): boolean {
        return !!this.#displayedCanvas;
    }

    /** @internal Whether a playback rasterize loop is currently running. */
    _isPlaybackRendering(): boolean {
        return this.#playbackRendering;
    }

    override get width() {
        return this.#width;
    }

    override set width(value: number) {
        if (this.#width === value) {
            return;
        }
        this.#width = value;
        this.#renderer.setSize(this.#width, this.#height);
        this.requiresRedraw = true;
    }

    override get height() {
        return this.#height;
    }

    override set height(value: number) {
        if (this.#height === value) {
            return;
        }
        this.#height = value;
        this.#renderer.setSize(this.#width, this.#height);
        this.requiresRedraw = true;
    }

    get maxWidth() {
        return this.#width;
    }

    set maxWidth(value: number) {
        this.width = value;
    }

    get maxHeight() {
        return this.#height;
    }

    set maxHeight(value: number) {
        this.height = value;
    }

    /** The raw composition HTML, written by the external authoring layer. */
    get html(): string {
        return this.#html;
    }

    set html(value: string) {
        if (this.#html === value) {
            return;
        }
        this.#html = value;
        this.#invalidateManagedHtml();
        this.requiresRedraw = true;
    }

    /**
     * Lint findings for the composition: static HTML-source rules plus runtime rules.
     *
     * `sceneDurationMs` is the scene's real playback length in milliseconds (our default time unit);
     * the caller supplies it (the composition timeline is normalized, so it can't be derived here).
     * Rules needing a real duration no-op without it.
     */
    async getLintFindings(sceneDurationMs?: number): Promise<LintFinding[]> {
        if (!this.#html) {
            return [];
        }
        const staticFindings = lintHtmlComposition(this.#html);
        return this.#serializeIntrospection(async () => {
            const context = await this.#introspectionContext();
            if (!context) {
                return staticFindings;
            }
            // Capture measured frame snapshots for the render-based rules. The renderer is left
            // seeked at the last probe afterward, which is fine: the DOM rules read source, not pose.
            // With no timeline the composition is static, so a single frame is enough — measuring
            // every probe would just repeat identical work.
            const frames = await captureFrames(this.#renderer, context.timeline ? undefined : [0]);
            const runtimeFindings = await runRuntimeLintRules({
                ...context,
                sourceHtml: this.#html,
                frames,
                canvasSize: canvasSizeOf(context.document),
                sceneDurationMs,
            });
            return [...staticFindings, ...runtimeFindings];
        });
    }

    /**
     * Run `task` against the loaded composition — its document, its timeline, and the ability to
     * seek and rasterize it — and return whatever the task returns. Null when there is nothing to
     * load (no HTML, or the iframe never produced a document).
     *
     * This is how an external layer inspects a composition it did not render: the app's design lint
     * measures frames through it. The task runs on the rasterize queue, so it never moves the pose
     * out from under an in-flight rasterize; it starts at the frame the asset is displaying, and the
     * composition is put back there once the task finishes.
     */
    async introspectComposition<T>(
        task: (composition: CompositionIntrospection) => Promise<T>,
    ): Promise<T | null> {
        return this.#serializeIntrospection(async () => {
            const context = await this.#introspectionContext();
            if (!context) {
                return null;
            }
            this.#renderer.seek(this.#progress);
            this.#externalTaskRunning = true;
            try {
                return await task({
                    document: context.document,
                    timeline: context.timeline,
                    seek: (progress: number) => this.#renderer.seek(progress),
                    rasterize: async (scale: number) => this.#renderer.rasterize(scale),
                });
            } finally {
                this.#externalTaskRunning = false;
                this.#renderer.seek(this.#progress);
            }
        });
    }

    // Runs an introspection task on the rasterize queue, so it never touches the iframe pose while
    // a snapDOM rasterize is mid-flight (and vice-versa).
    async #serializeIntrospection<T>(task: () => Promise<T>): Promise<T> {
        if (this.#externalTaskRunning) {
            throw new Error(
                'HtmlAsset: an introspectComposition task cannot call back into the asset — the nested call would wait on the task making it.',
            );
        }
        const run = this.#rasterizeChain.then(task);
        this.#rasterizeChain = run.then(
            () => undefined,
            () => undefined,
        );
        return run;
    }

    /** Replace an editable node's text by `id`, persisted into `html`. */
    setEditableText(id: string, value: string): void {
        this.html = replaceEditableText(this.#html, id, value);
    }

    /**
     * Editable text nodes visible at the frame the composition is currently rendered to, with their
     * text and bounds. Cached from the last at-rest (seek/preview) render — not recomputed during
     * playback — and refreshed as the playhead/scrubber moves, so the editor reads it synchronously
     * and reactively (mirrors how `boundsWithoutAnimation` is consumed).
     */
    get renderedEditableTextRegions(): EditableTextRegion[] {
        return this.#renderedEditableTextRegions;
    }

    /**
     * Non-editable text nodes (`data-l5-editable="false"`) visible at the frame the composition is
     * currently rendered to, with their bounds. Cached from the last at-rest render alongside
     * `renderedEditableTextRegions`, so the editor reads it synchronously and reactively.
     */
    get renderedNonEditableTextRegions(): NonEditableTextRegion[] {
        return this.#renderedNonEditableTextRegions;
    }

    /**
     * On-demand measure at the current frame (loads the iframe first). Prefer the synchronous,
     * cached `renderedEditableTextRegions` in the editor; this is for one-off/programmatic reads.
     */
    async getEditableTextRegions(): Promise<EditableTextRegion[]> {
        return this.#serializeIntrospection(async () => {
            const context = await this.#introspectionContext();
            if (!context) {
                return [];
            }
            this.#renderer.seek(this.#progress);
            return this.#measureEditableRegions(context.document);
        });
    }

    async getCodeoAssetRegions(): Promise<CodeoAssetRegion[]> {
        return this.#serializeIntrospection(async () => {
            const context = await this.#introspectionContext();
            if (!context) {
                return [];
            }
            this.#renderer.seek(this.#progress);
            return this.#measureCodeoAssetRegions(context.document);
        });
    }

    // Measure the editable nodes at the composition's current DOM frame (bounds + visibility).
    // Synchronous; the caller must have loaded the iframe and seeked it to the frame.
    #measureEditableRegions(document: Document): EditableTextRegion[] {
        const viewportWidth = document.documentElement.clientWidth;
        const viewportHeight = document.documentElement.clientHeight;
        const view = document.defaultView;
        return Array.from(document.querySelectorAll('[data-l5-editable="true"]'))
            .filter((node) => isNodeVisible(node, viewportWidth, viewportHeight))
            .map((node) => {
                const rect = node.getBoundingClientRect();
                // m41/m42 are the composed transform's translation. That equals the CSS translate()
                // only for a pure translation — which is what the editor's move writes back (and
                // editable text is statically positioned), so the accumulate-and-reapply round-trips.
                const transform = view?.getComputedStyle(node).transform;
                const matrix =
                    view && transform && transform !== 'none'
                        ? new view.DOMMatrix(transform)
                        : null;
                // Content taller than the box (a fixed height shorter than the text) — scrollHeight is
                // the full content extent, clientHeight the box; auto-height nodes grow so they match.
                const element = node as HTMLElement;
                return {
                    id: node.id,
                    text: node.textContent ?? '',
                    bounds: { x: rect.left, y: rect.top, width: rect.width, height: rect.height },
                    translate: { x: matrix?.m41 ?? 0, y: matrix?.m42 ?? 0 },
                    clipping: element.scrollHeight > element.clientHeight + 1,
                };
            });
    }

    #measureCodeoAssetRegions(document: Document): CodeoAssetRegion[] {
        const viewportWidth = document.documentElement.clientWidth;
        const viewportHeight = document.documentElement.clientHeight;
        return Array.from(document.querySelectorAll('[data-codeo-asset]'))
            .filter((node) => isNodeVisible(node, viewportWidth, viewportHeight))
            .map((node) => {
                const rect = node.getBoundingClientRect();
                const element = node as HTMLElement;
                return {
                    label: element.getAttribute('data-codeo-asset') ?? '',
                    bounds: { x: rect.left, y: rect.top, width: rect.width, height: rect.height },
                    outerHtml: element.outerHTML,
                };
            });
    }

    // Measure the non-editable (`data-l5-editable="false"`) nodes at the current DOM frame. Only
    // bounds and id are needed: the editor uses them to explain the text can't be edited in place.
    #measureNonEditableRegions(document: Document): NonEditableTextRegion[] {
        const viewportWidth = document.documentElement.clientWidth;
        const viewportHeight = document.documentElement.clientHeight;
        return Array.from(document.querySelectorAll('[data-l5-editable="false"]'))
            .filter((node) => isNodeVisible(node, viewportWidth, viewportHeight))
            .map((node) => {
                const rect = node.getBoundingClientRect();
                return {
                    id: node.id,
                    bounds: { x: rect.left, y: rect.top, width: rect.width, height: rect.height },
                };
            });
    }

    /** Merge CSS declarations into an editable node's inline style, persisted into `html`. */
    setEditableStyle(id: string, styles: Record<string, string>): void {
        this.html = applyEditableStyle(this.#html, id, styles);
    }

    /**
     * Live-preview a style change without persisting: mutate the loaded iframe DOM node directly (no
     * `#html` change, so no reload) and re-render the scene. Transient — the next `#html` reload (e.g.
     * a `setEditableStyle` commit) reverts it. An empty value clears that declaration; no-op until loaded.
     */
    previewEditableStyle(id: string, styles: Record<string, string>): void {
        const node = this.#renderer.contentDocument?.getElementById(id);
        if (!node) {
            return;
        }
        for (const [prop, value] of Object.entries(styles)) {
            (node as HTMLElement).style.setProperty(prop, value);
        }
        this.#requestReflow();
    }

    /**
     * Live-preview edited text as the user types: set the node's content on the loaded iframe DOM
     * directly (no `#html` change, so no reload) and re-render the scene, so the composition reflows
     * around it. Transient — blur's `setEditableText` commit persists it. No-op until loaded.
     */
    previewEditableText(id: string, text: string): void {
        const node = this.#renderer.contentDocument?.getElementById(id);
        if (!node) {
            return;
        }
        (node as HTMLElement).innerHTML = editableValueToHtml(text);
        this.#requestReflow();
    }

    // A preview mutates the offscreen composition DOM with no #html change, so nothing in the update
    // flow fires; ask the scene to re-render (re-raster) the current frame directly.
    #requestReflow(): void {
        this.slide?._emitViewChanged();
    }

    /**
     * Set (or clear, with `null`) the editable region being edited. Only that node's glyphs are
     * hidden in the raster (layout preserved) so the editor's overlay is its only visible copy.
     */
    async setEditingRegion(regionId: string | null): Promise<void> {
        if (this.#editingRegionId === regionId) {
            return;
        }
        this.#editingRegionId = regionId;
        this.#invalidateManagedHtml();
        this.requiresRedraw = true;
        await this.#rasterizeAtProgress(this.#progress, false);
    }

    get hasHtmlTextEditingEnabled(): boolean {
        return this.#htmlTextEditManager !== null;
    }

    /**
     * Enter in-place editing of a single region: hide that node's raster glyphs and inject a
     * `contentEditable` overlay for it into `domContainer`. The caller should have seeked to the
     * frame being edited first. Edits commit on blur.
     */
    async enableHtmlTextInput(
        domContainer: HTMLElement,
        activeRegionId: string,
        caretPoint?: CaretPoint,
    ): Promise<void> {
        // Validate the region before tearing down any current session, so a stale id can't kill it.
        const hasRegion = await this.#serializeIntrospection(async () => {
            const context = await this.#introspectionContext();
            if (!context) {
                return false;
            }
            const node = context.document.getElementById(activeRegionId);
            return node !== null && node.getAttribute('data-l5-editable') === 'true';
        });
        if (!hasRegion) {
            return;
        }
        if (this.#htmlTextEditManager) {
            await this.disableHtmlTextInput();
        }
        const region = await this.#serializeIntrospection(async () => {
            const context = await this.#introspectionContext();
            if (!context) {
                return null;
            }
            return this.#measureRegion(context.document, activeRegionId);
        });
        if (!region) {
            return;
        }

        await this.setEditingRegion(activeRegionId);

        const bounds = this.approximateMaxBounds;
        this.#htmlTextEditManager = new HtmlTextEditManager(domContainer, {
            assetBounds: { x: bounds.x, y: bounds.y, width: bounds.width, height: bounds.height },
            referenceResolution: this.getReferenceResolution(),
            regions: [region],
            commit: (id, value) => this.setEditableText(id, value),
            preview: (id, value) => this.previewEditableText(id, value),
        });
        this.#htmlTextEditManager.focus(activeRegionId, caretPoint);
    }

    /** Exit text editing: commit pending edits, tear down the overlays, and restore the raster glyphs. */
    async disableHtmlTextInput(): Promise<void> {
        if (!this.#htmlTextEditManager) {
            return;
        }
        // Re-raster with glyphs restored before removing the overlays, so the text never flashes empty.
        this.#htmlTextEditManager.commitAll();
        await this.setEditingRegion(null);
        this.#htmlTextEditManager.removeOverlays();
        this.#htmlTextEditManager = null;
    }

    // Measure one editable region from the loaded iframe; null when the id isn't an editable node.
    #measureRegion(doc: Document, regionId: string): EditableRegionDescriptor | null {
        const node = doc.getElementById(regionId);
        if (!node || node.getAttribute('data-l5-editable') !== 'true') {
            return null;
        }
        const rect = node.getBoundingClientRect();
        const computed = doc.defaultView?.getComputedStyle(node);
        const style: Record<string, string> = {};
        for (const prop of EDITABLE_OVERLAY_STYLE_PROPS) {
            style[prop] = (computed as unknown as Record<string, string>)?.[prop] ?? '';
        }
        return {
            id: node.id,
            // innerText (not textContent) so existing <br> line breaks come through as `\n`.
            text: (node as HTMLElement).innerText,
            bounds: { x: rect.left, y: rect.top, width: rect.width, height: rect.height },
            // The region may grow from its top down to the bottom of the frame before it's "full".
            maxContentHeight: Math.max(0, this.#height - rect.top),
            style,
        };
    }

    // Ensure the composition is loaded and hand back its document + timeline; null when there's nothing to load.
    async #introspectionContext(): Promise<{
        document: Document;
        timeline: GsapTimelineLike | null;
    } | null> {
        await this.#resolveFonts();
        this.#refreshManagedHtml();
        if (!this.#managedHtml) {
            return null;
        }
        await this.#loadManagedHtml();
        const document = this.#renderer.contentDocument;
        if (!document) {
            return null;
        }
        return {
            document,
            timeline: this.#renderer.getTimelineObject() as GsapTimelineLike | null,
        };
    }

    /** The auto-managed HTML actually loaded into the iframe (template colors injected, etc.). */
    get managedHtml(): string {
        this.#refreshManagedHtml();
        return this.#managedHtml;
    }

    override _hasTemplateReferenceValues(): boolean {
        return new RegExp(
            `var\\(\\s*(${TEMPLATE_COLOR_VAR_PREFIX}|${TEMPLATE_FONT_VAR_PREFIX})`,
        ).test(this.#html);
    }

    getVisibleResolution(): { width: number; height: number } {
        return { width: this.#width, height: this.#height };
    }

    override get approximateMaxBounds(): PIXI.Rectangle {
        const freeParent = this.getFreelyPositionedParent();
        const transformation = freeParent.transformation;
        const { width, height } = this.getVisibleResolution();
        const { x, y } = getPositionFromTransformation(transformation, width, height);
        return { x, y, width, height } as PIXI.Rectangle;
    }

    override applyTransformation(offsetX = 0, offsetY = 0, offsetRotation = 0) {
        this._pixiContainer.alpha = this.opacity;
        if (this.isPositionControlledByParent) {
            return;
        }
        const { width, height } = this.getVisibleResolution();
        const { x, y } = getPositionFromTransformation(
            this.transformation.save() as TransformationType,
            width,
            height,
        );

        this._pixiContainer.x = x + offsetX;
        this._pixiContainer.y = y + offsetY;
        this._pixiContainer.rotation = this.transformation.rotation + offsetRotation;
    }

    #getPalette(): string[] {
        const colors: string[] = [];
        if (this._templateOrNull?.colors) {
            for (const templateColor of this.template.colors) {
                colors.push(templateColorToHex(templateColor.color));
            }
        }
        return colors;
    }

    // The template-font slots the composition references via `var(--l5-font-N)`, mapped to their URLs.
    #fontSlots(): HtmlFontSlot[] {
        const urls = this._templateOrNull?.fonts ?? [];
        const indices = new Set<number>();
        for (const match of this.#html.matchAll(
            new RegExp(`${TEMPLATE_FONT_VAR_PREFIX}(\\d+)`, 'g'),
        )) {
            indices.add(Number(match[1]));
        }
        return [...indices]
            .filter((index) => index >= 0 && index < urls.length)
            .map((index) => ({ index, url: urls[index] }));
    }

    // Fetch the referenced template fonts (cached module-wide) so #refreshManagedHtml can inject the
    // resolved faces synchronously.
    async #resolveFonts(): Promise<void> {
        await resolveHtmlFontFaces(this.#fontSlots());
    }

    #invalidateManagedHtml(): void {
        this.#managedHtml = '';
        this.#managedSourceHtml = null;
        this.#loadedManagedHtml = null;
        // #displayedCanvas is kept so the last frame holds until the next render replaces it.
    }

    #refreshManagedHtml(): boolean {
        if (!this.#html) {
            const had = this.#managedHtml !== '';
            this.#managedHtml = '';
            this.#managedSourceHtml = null;
            return had;
        }
        const palette = this.#getPalette();
        const paletteKey = palette.join('|');
        const fonts = getResolvedHtmlFontFaces(this.#fontSlots());
        const fontsKey = fonts.map((font) => `${font.index}:${font.familyName}`).join('|');
        if (
            this.#managedSourceHtml === this.#html &&
            this.#paletteKey === paletteKey &&
            this.#fontsKey === fontsKey &&
            this.#managedEditingRegionId === this.#editingRegionId &&
            this.#managedHtml
        ) {
            return false;
        }
        this.#managedHtml = manageComposition(this.#html, palette, {
            editingRegionId: this.#editingRegionId,
            fonts,
        });
        this.#managedSourceHtml = this.#html;
        this.#paletteKey = paletteKey;
        this.#fontsKey = fontsKey;
        this.#managedEditingRegionId = this.#editingRegionId;
        return true;
    }

    async #loadManagedHtml(): Promise<void> {
        if (this.#loadedManagedHtml === this.#managedHtml) {
            return;
        }
        this.#renderer.setSize(this.#width, this.#height);
        await this.#renderer.load(this.#managedHtml);
        this.#loadedManagedHtml = this.#managedHtml;
    }

    // Playback render, latest-wins: (re)render the current progress into the displayed frame. If a
    // render is already in flight, just re-arm — the running loop will pick up the newest progress
    // when it finishes, so we never queue a backlog and always show the freshest completed frame.
    #requestPlaybackRender(): void {
        this.#playbackRenderPending = true;
        if (this.#playbackRendering) {
            return;
        }
        this.#playbackRendering = true;
        // Serialize with the awaited (seek/export) path — the shared iframe can't capture concurrently.
        const run = this.#rasterizeChain.then(async () => this.#drainPlaybackRenders());
        this.#rasterizeChain = run.catch(() => undefined);
    }

    async #drainPlaybackRenders(): Promise<void> {
        try {
            while (this.#playbackRenderPending && !this.destroyed) {
                this.#playbackRenderPending = false;
                const canvas = await this.#seekAndRasterize(this.#progress);
                if (canvas) {
                    this.#displayedCanvas = canvas;
                    this.requiresRedraw = true;
                }
            }
        } catch {
            // Hold the last displayed frame; the next tick re-arms a render.
        } finally {
            this.#playbackRendering = false;
        }
    }

    // Seek + rasterize at output density, shared by playback and the awaited buffer path.
    async #seekAndRasterize(progress: number): Promise<HTMLCanvasElement | null> {
        await this.#resolveFonts();
        this.#refreshManagedHtml();
        if (!this.#managedHtml) {
            return null;
        }
        await this.#loadManagedHtml();
        this.#renderer.seek(progress);
        this.#syncActiveOverlayBounds();
        const canvas = await this.#renderer.rasterize(this.referenceToOutputMultiplier);
        return this.destroyed ? null : canvas;
    }

    // Re-measure the active region after the iframe re-lays-out so its overlay stays glued to the raster.
    #syncActiveOverlayBounds(): void {
        if (!this.#htmlTextEditManager || !this.#editingRegionId) {
            return;
        }
        const doc = this.#renderer.contentDocument;
        if (!doc) {
            return;
        }
        const region = this.#measureRegion(doc, this.#editingRegionId);
        if (region) {
            this.#htmlTextEditManager.reposition(region.id, region.bounds, region.maxContentHeight);
        }
    }

    // The awaited path for seeks and export: rasterize into the displayed-frame slot.
    async #rasterizeAtProgress(progress: number, updateRegionCache: boolean): Promise<void> {
        const run = this.#rasterizeChain.then(async () => {
            try {
                const canvas = await this.#seekAndRasterize(progress);
                if (!canvas) {
                    return;
                }
                this.#displayedCanvas = canvas;
                // Only refresh the editable-region cache for at-rest single-frame renders (the frame
                // the editor displays). Playback buffering (ResolveWhenCanPlay) also lands here — its
                // initial seek to the scene start would otherwise clobber the cache with the regions
                // at frame 0, which then survives the whole playback and the editor re-mount on stop.
                // Readers pick up the new value on the `onRenderedSlideImage` event this render emits.
                if (updateRegionCache) {
                    const doc = this.#renderer.contentDocument;
                    if (doc) {
                        this.#renderedEditableTextRegions = this.#measureEditableRegions(doc);
                        this.#renderedNonEditableTextRegions = this.#measureNonEditableRegions(doc);
                    }
                }
            } catch {
                // Hold the last displayed frame; the editor re-buffers on the next seek.
            }
        });
        this.#rasterizeChain = run.catch(() => undefined);
        return run;
    }

    override async _bufferAtTime(
        time: number,
        bufferingMode = BufferingMode.ResolveWhenSingleFrame,
    ): Promise<void> {
        if (!this._shouldLoad || !this.#html) {
            return;
        }
        const duration = this.container?.duration;
        const progress = duration && duration > 0 ? clamp01(time / duration) : this.#progress;
        this.#progress = progress;
        await this.#rasterizeAtProgress(
            progress,
            bufferingMode === BufferingMode.ResolveWhenSingleFrame,
        );
    }

    override updateForRender() {
        // Draw the latest completed frame. Playback rendering fills #displayedCanvas asynchronously
        // (latest-wins); this just composites whatever is currently ready, holding the last frame
        // until a fresher one lands.
        if (!this.visible || !this.#displayedCanvas) {
            return;
        }

        this.#sprite.texture = this._reusableTextures.allocateTexture(() => {
            resetSprite(this.#sprite);
        });

        const sharedTexture = this.#sprite.texture;
        const canvas = (sharedTexture.baseTexture.resource as PIXI.BaseImageResource)
            .source as HTMLCanvasElement;

        // The shared texture is video-frame sized, not asset sized; fit the asset's aspect ratio.
        const assetAspect = this.#width / this.#height;
        const baseTexW = sharedTexture.width;
        const baseTexH = sharedTexture.height;
        let frameW: number;
        let frameH: number;
        if (baseTexW / baseTexH > assetAspect) {
            frameH = baseTexH;
            frameW = frameH * assetAspect;
        } else {
            frameW = baseTexW;
            frameH = frameW / assetAspect;
        }
        const canvasScale = canvas.width / baseTexW;
        const destWidthPx = frameW * canvasScale;
        const destHeightPx = frameH * canvasScale;

        const ctx = canvas.getContext('2d');
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
        ctx?.resetTransform();
        ctx?.drawImage(this.#displayedCanvas, 0, 0, destWidthPx, destHeightPx);

        const frame = new PIXI.Rectangle(0, 0, frameW, frameH);
        sharedTexture.frame = frame;
        sharedTexture.orig = frame;
        sharedTexture.trim = frame;

        this.#sprite.width = this.#width;
        this.#sprite.height = this.#height;

        sharedTexture.update();
    }

    override _build() {
        this.clearAnimation();
        super._build();
        // Asset-level animation only; the composition self-animates via progress.
        this.applyTransformation();
        this._applyAnimation();
        this._applyIntroAnimations();
        this._applyOutroAnimations();
        this.animationManager.setInitValues();
    }

    override _draw() {
        this.#sprite.width = this.#width;
        this.#sprite.height = this.#height;
    }

    override _seek(progress: number, seekMode: SeekMode) {
        super._seek(progress, seekMode);
        this.#progress = clamp01(progress);
        // Playback advances the playhead without buffering, so drive rendering from the seek itself.
        if (this.#html && this.luminaryParent?.isPlaying) {
            this.#requestPlaybackRender();
        }
    }

    _htmlCustomSave(): string {
        return this.#html;
    }

    _htmlCustomLoad(data: string | null, _settings: LoadSettings) {
        this.#html = data ?? '';
        this.#invalidateManagedHtml();
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(['mask', 'maxWidth', 'maxHeight', 'html']);
    }
}
